import classnames from 'classnames';
import { useDialog } from '@sorrem-client/ui/components';
import { useCoversScreen, useCutscene } from '@sorrem-client/ui/hooks';
import type { Cutscene, CutscenePanel } from '@sorrem/shared';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { CutsceneAudioPlayer } from './cutscene-audio';
import {
    resolveAutoAdvanceMs,
    resolveCameraEndStyle,
    resolveCameraStyle,
    resolveImageObjectPosition,
    resolvePreviousBeat,
    resolveTextPlacement,
} from './cutscene-panel';
import styles from './CutscenePlayer.module.scss';

/**
 * How a cutscene ended: played to its last panel, skipped, or never shown
 * because it failed to load. Only a failed one stays unseen, so it plays the
 * next time.
 */
export type CutsceneOutcome = 'watched' | 'skipped' | 'failed';

interface CutscenePlayerProps {
    cutsceneId: string;
    /** Called once, with how the cutscene ended. */
    onFinished: (outcome: CutsceneOutcome) => void;
}

interface CutsceneScreenProps {
    /** The cutscene to play, its media paths URLs; null while it loads. */
    cutscene: Cutscene | null;
    /** It failed to load: it finishes at once as 'failed'. */
    isError: boolean;
    /** Called once, with how the cutscene ended. */
    onFinished: (outcome: CutsceneOutcome) => void;
    /** The panel it opens on; the first by default. */
    startPanelIndex?: number;
}

/** Keys that advance to the next line or panel. */
const ADVANCE_KEYS = new Set([' ', 'Enter', 'ArrowRight']);

/** Loads a cutscene the current character may see, then plays it (`CutsceneScreen`). */
export function CutscenePlayer({ cutsceneId, onFinished }: CutscenePlayerProps): JSX.Element {
    const { cutscene, isError } = useCutscene(cutsceneId);
    return <CutsceneScreen cutscene={cutscene} isError={isError} onFinished={onFinished} />;
}

/**
 * Full-screen cutscene: one panel at a time (a still image, text and sound
 * effects). Each line of text is its own beat, shown as a balloon placed by
 * who says it; a click/tap or key shows the next line, and after the last
 * line moves to the next panel (or the panel advances itself when it says so).
 * It is a modal dialog (`useDialog`): it takes keyboard focus while open and
 * keeps Tab inside, so key presses don't reach the game behind it.
 */
export function CutsceneScreen({ cutscene, isError, onFinished, startPanelIndex = 0 }: CutsceneScreenProps): JSX.Element {
    const dialog = useDialog<HTMLDivElement>();
    useCoversScreen();
    const [panelIndex, setPanelIndex] = useState(startPanelIndex);
    // Which of the panel's lines is showing.
    const [lineIndex, setLineIndex] = useState(0);
    // The panel being crossfaded away from, kept until its fade-out ends.
    const [outgoingPanel, setOutgoingPanel] = useState<CutscenePanel | null>(null);
    const [failedImageSrcs, setFailedImageSrcs] = useState<ReadonlySet<string>>(() => new Set());
    const audioRef = useRef<CutsceneAudioPlayer | null>(null);

    // Guards against finishing twice (e.g. an auto-advance racing a click).
    const hasFinishedRef = useRef(false);
    const onFinishedRef = useRef(onFinished);
    onFinishedRef.current = onFinished;
    const finish = useCallback((outcome: CutsceneOutcome): void => {
        if (hasFinishedRef.current) {
            return;
        }
        hasFinishedRef.current = true;
        audioRef.current?.dispose();
        onFinishedRef.current(outcome);
    }, []);

    const panels = cutscene?.panels ?? [];
    const panel = panels[panelIndex] ?? null;
    const lines = panel?.text ?? [];
    const line = lines[lineIndex] ?? null;
    const isLastLine = lineIndex >= lines.length - 1;

    const advance = useCallback((): void => {
        if (cutscene === null) {
            return;
        }
        if (!isLastLine) {
            setLineIndex(lineIndex + 1);
            return;
        }
        setLineIndex(0);
        if (panelIndex >= cutscene.panels.length - 1) {
            finish('watched');
            return;
        }
        const nextPanel = cutscene.panels[panelIndex + 1];
        setOutgoingPanel(nextPanel?.enter === 'crossfade' ? cutscene.panels[panelIndex] ?? null : null);
        setPanelIndex(panelIndex + 1);
    }, [cutscene, finish, isLastLine, lineIndex, panelIndex]);

    const rewind = useCallback((): void => {
        const previous = cutscene !== null ? resolvePreviousBeat(cutscene.panels, { panelIndex, lineIndex }) : null;
        if (previous === null) {
            return;
        }
        // Cut straight back: crossfading in reverse would read as moving on.
        setOutgoingPanel(null);
        setPanelIndex(previous.panelIndex);
        setLineIndex(previous.lineIndex);
    }, [cutscene, lineIndex, panelIndex]);

    // A cutscene that can't load is passed over (story must never block play), and stays unseen.
    useEffect(() => {
        if (isError) {
            finish('failed');
        }
    }, [finish, isError]);

    useEffect(() => {
        const audio = new CutsceneAudioPlayer();
        audioRef.current = audio;
        return (): void => {
            audio.dispose();
            audioRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (cutscene?.music) {
            audioRef.current?.playMusic(cutscene.music);
        }
    }, [cutscene]);

    useEffect(() => {
        if (panel === null) {
            return undefined;
        }
        audioRef.current?.playPanelSfx(panel.sfx ?? []);
        return (): void => {
            audioRef.current?.stopPanelSfx();
        };
    }, [panel]);

    // A self-advancing panel waits for its last line, then moves on.
    useEffect(() => {
        if (panel === null || !isLastLine) {
            return undefined;
        }
        const autoAdvanceMs = resolveAutoAdvanceMs(panel);
        if (autoAdvanceMs === null) {
            return undefined;
        }
        const timerId = window.setTimeout(advance, autoAdvanceMs);
        return (): void => {
            window.clearTimeout(timerId);
        };
    }, [advance, isLastLine, panel]);

    // Warm the next panel's image so it doesn't pop in after the transition.
    const nextImageSrc = panels[panelIndex + 1]?.image?.src;
    useEffect(() => {
        if (nextImageSrc !== undefined) {
            new Image().src = nextImageSrc;
        }
    }, [nextImageSrc]);

    const { onKeyDown: handleDialogKeyDown } = dialog;
    const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>): void => {
        handleDialogKeyDown(event);
        if (event.repeat) {
            return;
        }
        if (event.key === 'Escape' && cutscene?.isSkippable) {
            event.preventDefault();
            finish('skipped');
            return;
        }
        if (ADVANCE_KEYS.has(event.key) && event.target === event.currentTarget) {
            event.preventDefault();
            advance();
            return;
        }
        // Steps back a line or panel. Not hinted on screen, like the right arrow.
        if (event.key === 'ArrowLeft' && event.target === event.currentTarget) {
            event.preventDefault();
            rewind();
        }
    }, [advance, cutscene?.isSkippable, finish, handleDialogKeyDown, rewind]);

    const handleImageError = useCallback((src: string): void => {
        setFailedImageSrcs((current) => new Set(current).add(src));
    }, []);

    const isTapToAdvance = panel !== null && (!isLastLine || resolveAutoAdvanceMs(panel) === null);
    const placement = line !== null ? resolveTextPlacement(line) : null;

    // Rendered into the body: a full-screen overlay placed inside a panel that
    // uses a CSS filter or transform would otherwise be clipped to that panel.
    return createPortal(
        <div
            ref={dialog.ref}
            className={styles.screen}
            role="dialog"
            aria-modal="true"
            aria-label={cutscene?.title ?? 'Cutscene'}
            tabIndex={-1}
            onClick={advance}
            onKeyDown={handleKeyDown}
        >
            {outgoingPanel?.image && (
                <PanelImage
                    key={`outgoing-${outgoingPanel.id}`}
                    panel={outgoingPanel}
                    className={styles.outgoingImage}
                    isFailed={failedImageSrcs.has(outgoingPanel.image.src)}
                    isOutgoing
                    onError={handleImageError}
                    onAnimationEnd={() => setOutgoingPanel(null)}
                />
            )}

            {panel !== null && (
                <div key={panel.id} className={classnames(styles.panel, styles[panel.enter ?? 'cut'])}>
                    {panel.image && (
                        <PanelImage
                            panel={panel}
                            className={styles.image}
                            isFailed={failedImageSrcs.has(panel.image.src)}
                            onError={handleImageError}
                        />
                    )}

                    {line !== null && placement !== null && (
                        <p
                            key={`${panel.id}-${lineIndex}`}
                            className={classnames(styles.balloon, styles[placement.area], styles[line.kind])}
                            style={placement.area === 'speaker' ? placement.style : undefined}
                            aria-live="polite"
                        >
                            {line.kind === 'dialogue' && line.speaker && (
                                <span className={styles.speakerName}>{line.speaker}</span>
                            )}
                            {line.body}
                        </p>
                    )}
                </div>
            )}

            <div className={styles.footer}>
                <span>
                    {panels.length > 0 ? `${panelIndex + 1} / ${panels.length}` : ''}
                </span>
                {isTapToAdvance && <span className={styles.hint}>Tap or press Space to continue</span>}
                {cutscene?.isSkippable && <span className={styles.hint}>Esc to skip</span>}
            </div>
        </div>,
        document.body,
    );
}

interface PanelImageProps {
    panel: CutscenePanel;
    className?: string;
    isFailed: boolean;
    /** Fading out: hold the camera's final framing instead of replaying its move. */
    isOutgoing?: boolean;
    onError: (src: string) => void;
    onAnimationEnd?: () => void;
}

/** A panel's still image, or its alt text if the image failed to load. */
function PanelImage({
    panel,
    className,
    isFailed,
    isOutgoing = false,
    onError,
    onAnimationEnd,
}: PanelImageProps): JSX.Element | null {
    const image = panel.image;
    if (!image) {
        return null;
    }
    if (isFailed) {
        return <p className={styles.imageFallback}>{image.alt}</p>;
    }
    const camera = image.camera;
    const cameraStyle = camera ? (isOutgoing ? resolveCameraEndStyle(camera) : resolveCameraStyle(camera)) : {};
    const hasCamera = camera && !isOutgoing;
    return (
        <img
            className={classnames(className, image.fit === 'contain' ? styles.contain : styles.cover, hasCamera && styles.camera)}
            src={image.src}
            alt={image.alt}
            style={{ objectPosition: resolveImageObjectPosition(image.focus), ...cameraStyle }}
            draggable={false}
            onError={() => onError(image.src)}
            onAnimationEnd={onAnimationEnd}
        />
    );
}
